"use client";

import type * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Layout shell for the MCP server picker. Pure presentational composition —
 * the consumer (ui-kit) supplies the molecules (search input, filter chips,
 * server cards, summary chips, etc.) and all the domain data + callbacks
 * via children. The shell only owns spacing, container queries, and the
 * `data-slot` hooks for queryability.
 *
 * Typical composition:
 *
 *   <McpServerPicker>
 *     <McpServerPickerHeader>
 *       <Input ... />
 *       <McpCategoryFilter ... />
 *       <McpUnoptimizedToggle ... />
 *     </McpServerPickerHeader>
 *     <McpSelectionSummaryBar ... />
 *     <McpServerPickerGrid>
 *       {servers.map(s => <McpServerCard key={s.id} ... />)}
 *     </McpServerPickerGrid>
 *   </McpServerPicker>
 *
 * Each subcomponent is a thin <div> with consistent layout — no internal
 * state, no domain knowledge. Selection / search / filter behavior belongs
 * in the consumer (ui-kit), where the picker's domain state already lives
 * (`useToolSelection`, `usePickerSearch`, etc.).
 */
export function McpServerPicker({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn("@container space-y-3", className)}
      data-slot="mcp-server-picker"
    />
  );
}

/**
 * Toolbar row — search input, category filter, unoptimized toggle, etc.
 * Stacks on narrow containers, flows inline at `@3xl`.
 */
export function McpServerPickerHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn(
        "flex @3xl:flex-row flex-col @3xl:items-center @3xl:gap-3 gap-2",
        className
      )}
      data-slot="mcp-server-picker-header"
    />
  );
}

/**
 * Responsive grid of server cards. 1 col by default, 2 at `@md`, 3 at
 * `@3xl`, 4 at `@5xl` — sized to comfortably fit `McpServerCard` widths.
 */
export function McpServerPickerGrid({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn(
        "grid @3xl:grid-cols-3 @5xl:grid-cols-4 @md:grid-cols-2 grid-cols-1 gap-2",
        className
      )}
      data-slot="mcp-server-picker-grid"
    />
  );
}

/**
 * Responsive grid of tool cards (denser than the server grid). 1 col by
 * default, 2 at `@xl`, 3 at `@4xl` — sized to fit `McpToolCard` widths.
 * Used inside drill-in views and grouped tool sections.
 */
export function McpServerPickerToolGrid({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn(
        "grid @4xl:grid-cols-3 @xl:grid-cols-2 grid-cols-1 gap-2",
        className
      )}
      data-slot="mcp-server-picker-tool-grid"
    />
  );
}
